import { Capacitor } from "@capacitor/core";
import { Browser } from "@/lib/capacitor/browser";
import { logger } from "@/logger";

/**
 * Validates if a URL is safe for downloading/opening
 *
 * @param url - The URL to validate
 * @returns True if URL is valid and uses safe protocol (http/https)
 */
function isValidUrl(url: string): boolean {
  try {
    const urlObj = new URL(url);
    return ["https:", "http:"].includes(urlObj.protocol);
  } catch {
    return false;
  }
}

/**
 * Handle file downloads across different platforms
 *
 * **Use this function when you want to DOWNLOAD a file** (saves to device). For viewing files
 * in-app without downloading, use `openFile()` instead.
 *
 * Platform behavior:
 *
 * - **iOS/Android**: Opens URL in external browser where download is triggered
 * - **Web**: Creates download link and triggers browser download
 *
 * @example
 *   ```typescript
 *   // Download a PDF file
 *   await downloadFile('https://example.com/report.pdf', 'report.pdf');
 *   ```;
 *
 * @param url - The URL to download from (must be http/https)
 * @param filename - The filename to use for the download (used on web only)
 * @throws {Error} If URL is invalid or download fails
 */
export async function downloadFile(url: string, filename: string) {
  if (!isValidUrl(url)) {
    logger.error("Invalid download URL:", url);
    throw new Error("Invalid download URL");
  }

  if (Capacitor.isNativePlatform()) {
    try {
      // On iOS/Android, we need to open in external browser
      // Using location.href as it's more reliable than window.open on iOS WebView
      window.location.href = url;
    } catch (error) {
      logger.error("Native download failed:", error);
      throw new Error("Download failed");
    }
  } else {
    // On web, use the traditional download approach
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}

/**
 * Open a file URL for viewing across different platforms
 *
 * **Use this function when you want to VIEW a file** (displays in-app or new tab). For downloading
 * files to device, use `downloadFile()` instead.
 *
 * Platform behavior:
 *
 * - **iOS**: Opens in SFSafariViewController (in-app browser with close button)
 * - **Android**: Opens in Chrome Custom Tabs (in-app browser with close button)
 * - **Web**: Opens in new browser tab
 *
 * Users stay within app context on mobile with ability to close and return.
 *
 * @example
 *   ```typescript
 *   // View a PDF document in-app
 *   await openFile('https://example.com/document.pdf');
 *   ```;
 *
 * @param url - The URL to open (must be http/https)
 * @throws {Error} If URL is invalid or browser fails to open
 */
export async function openFile(url: string) {
  if (!isValidUrl(url)) {
    logger.error("Invalid file URL:", url);
    throw new Error("Invalid file URL");
  }

  if (Capacitor.isNativePlatform()) {
    try {
      // On native platforms, use Capacitor Browser plugin for in-app viewing
      // This opens files in SFSafariViewController (iOS) or Chrome Custom Tabs (Android)
      await Browser.open({
        url,
        presentationStyle: "popover", // iOS: shows as modal that can be dismissed
      });
    } catch (error) {
      logger.error("Failed to open file in browser:", error);
      throw new Error("Failed to open file");
    }
  } else {
    // On web, open in a new tab
    const newWindow = window.open(url, "_blank", "noopener,noreferrer");
    if (!newWindow) {
      logger.error("Failed to open new window - popup may be blocked");
      throw new Error("Failed to open file - popup may be blocked");
    }
  }
}
